import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const statusColors = {
  pending: "#f59e0b",
  completed: "#10b981",
  expired: "#ef4444",
  duplicate: "#6b7280",
  unmatched: "#8b5cf6",
};

function DepositsManagement() {
  const [deposits, setDeposits] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const adminToken = localStorage.getItem("adminToken");

  useEffect(() => {
    fetchDeposits();
  }, [filter]);

  const fetchDeposits = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API_URL}/admin/deposits${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setDeposits(res.data);
    } catch {
      setMessage("Failed to load deposits");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Approve this deposit and credit the user?")) return;
    try {
      const res = await axios.post(
        `${API_URL}/admin/deposits/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setMessage(`✅ Approved! New balance: ${res.data.new_balance.toFixed(2)} Birr`);
      fetchDeposits();
    } catch (err) {
      setMessage("❌ " + (err.response?.data?.detail || "Approval failed"));
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Reason for rejection:", "Rejected by admin");
    if (!reason) return;
    try {
      await axios.post(
        `${API_URL}/admin/deposits/${id}/reject?reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setMessage("✅ Deposit rejected");
      fetchDeposits();
    } catch (err) {
      setMessage("❌ " + (err.response?.data?.detail || "Rejection failed"));
    }
  };

  return (
    <div style={{ padding: "24px", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 style={{ margin: 0 }}>💰 Deposit Management</h1>
        <Link to="/dashboard" style={{ textDecoration: "none", color: "#667eea", fontWeight: 600 }}>← Dashboard</Link>
      </div>

      {message && (
        <div style={{
          padding: "10px 16px", borderRadius: 8, marginBottom: 16,
          background: message.startsWith("✅") ? "#d1fae5" : "#fee2e2",
          color: message.startsWith("✅") ? "#065f46" : "#991b1b",
        }}>
          {message}
          <button onClick={() => setMessage("")} style={{ float: "right", background: "none", border: "none", cursor: "pointer" }}>✕</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["pending", "completed", "expired", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer",
              fontWeight: 600, fontSize: 13,
              background: filter === s ? "#667eea" : "#e5e7eb",
              color: filter === s ? "#fff" : "#374151",
            }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <button onClick={fetchDeposits} style={{ marginLeft: "auto", padding: "6px 14px", borderRadius: 6, border: "1px solid #d1d5db", cursor: "pointer", background: "#fff" }}>
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : deposits.length === 0 ? (
        <p style={{ color: "#6b7280" }}>No deposits found.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ background: "#f3f4f6" }}>
                {["ID", "Username", "Phone", "Telegram ID", "Amount", "Method", "Status", "Date", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {deposits.map((d) => (
                <tr key={d.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "10px 12px" }}>#{d.id}</td>
                  <td style={{ padding: "10px 12px" }}>{d.username || "—"}</td>
                  <td style={{ padding: "10px 12px" }}>{d.phone || "—"}</td>
                  <td style={{ padding: "10px 12px" }}>{d.telegram_id || "—"}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: "#059669" }}>{d.amount.toFixed(2)} Birr</td>
                  <td style={{ padding: "10px 12px", textTransform: "capitalize" }}>{d.method}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{
                      padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700,
                      background: statusColors[d.status] + "22",
                      color: statusColors[d.status],
                    }}>
                      {d.status}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px", color: "#6b7280", fontSize: 12 }}>
                    {d.created_at ? new Date(d.created_at).toLocaleString() : "—"}
                  </td>
                  <td style={{ padding: "10px 12px", display: "flex", gap: 6 }}>
                    {d.status === "pending" && (
                      <>
                        <button onClick={() => handleApprove(d.id)} style={{ padding: "4px 10px", borderRadius: 5, border: "none", background: "#10b981", color: "#fff", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                          ✓ Approve
                        </button>
                        <button onClick={() => handleReject(d.id)} style={{ padding: "4px 10px", borderRadius: 5, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                          ✕ Reject
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DepositsManagement;
